import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { adminAPI } from '../lib/api';
import { LogoMark } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { TrustBadge } from '../components/TrustBadge';
import DocumentHistory from '../components/DocumentHistory';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Users, 
  Tent, 
  MessageSquare, 
  ShieldCheck,
  Search,
  Check,
  X,
  Eye,
  AlertCircle,
  UserCog,
  Plus,
  FileText,
  Upload,
  Trash2,
  Mail,
  MailCheck,
  Flag,
  Star,
  ShieldAlert,
  ScrollText,
  Download,
  FileSpreadsheet,
  FileDown,
  CheckCircle2,
  XCircle,
  Clock,
  MonitorSmartphone,
  Pencil,
  History
} from 'lucide-react';
import { provinces, activities, campTypes, ageGroups, campRoles } from '../lib/utils';

const DOC_TYPE_LABELS = {
  id_document: 'ID Document',
  police_clearance: 'Police Clearance',
  qualification: 'Qualification',
};
const docTypeLabel = (t) => DOC_TYPE_LABELS[t] || (t ? t.replace(/_/g, ' ') : 'Document');

const formatDocDate = (iso) => {
  if (!iso) return 'Unknown date';
  try {
    return new Date(iso).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

const viewDocument = async (filename) => {
  // Open the tab synchronously (inside the click gesture) to avoid popup blockers
  const win = window.open('', '_blank', 'noopener,noreferrer');
  try {
    const res = await adminAPI.getDocument(filename);
    const url = URL.createObjectURL(res.data);
    if (win) {
      win.location = url;
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (win) win.close();
    toast.error('Failed to open document');
  }
};

const downloadDocument = async (filename, downloadName) => {
  try {
    const res = await adminAPI.getDocument(filename);
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadName || filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    toast.error('Failed to download document');
  }
};

const ADMIN_STATUS_BADGE = {
  pending: { label: 'Pending Review', cls: 'bg-amber-100 text-amber-800 border-amber-200', Icon: Clock },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-800 border-green-200', Icon: CheckCircle2 },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-800 border-red-200', Icon: XCircle },
};

const AdminDocStatusBadge = ({ status }) => {
  const cfg = ADMIN_STATUS_BADGE[status] || ADMIN_STATUS_BADGE.pending;
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${cfg.cls}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

const FacilitatorDocsDialog = ({ facilitator, open, onOpenChange, onReviewed }) => {
  const [docs, setDocs] = useState([]);
  const [rejectingId, setRejectingId] = useState(null);
  const [reason, setReason] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    setDocs(facilitator?.documents || []);
    setRejectingId(null);
    setReason('');
  }, [facilitator]);

  const applyReview = async (doc, status, rejectReason) => {
    setBusyId(doc.id);
    try {
      const res = await adminAPI.reviewDocument(facilitator.id, doc.id, status, rejectReason || null);
      const event = { type: res.data.review_status, at: res.data.reviewed_at };
      if (res.data.rejection_reason) event.reason = res.data.rejection_reason;
      setDocs((prev) => prev.map((d) =>
        d.id === doc.id
          ? { ...d, review_status: res.data.review_status, rejection_reason: res.data.rejection_reason, history: [...(d.history || []), event] }
          : d
      ));
      setRejectingId(null);
      setReason('');
      toast.success(`Document ${status === 'approved' ? 'approved' : status === 'rejected' ? 'rejected' : 'updated'}`);
      if (onReviewed) onReviewed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update document');
    }
    setBusyId(null);
  };

  const docList = facilitator?.documents ? docs : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Documents — {facilitator?.full_name}</DialogTitle>
          <DialogDescription>Admin-only verification documents. Every view is logged.</DialogDescription>
        </DialogHeader>
        {docList.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No documents uploaded.</p>
        ) : (
          <ul className="space-y-3 max-h-[60vh] overflow-y-auto">
            {docList.map((doc) => {
              const status = doc.review_status || 'pending';
              return (
                <li
                  key={doc.id || doc.filename}
                  className="border border-border rounded-xl p-3"
                  data-testid={`admin-doc-${doc.id || doc.filename}`}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <FileText className="w-4 h-4 text-primary" />
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                      {docTypeLabel(doc.type)}
                    </span>
                    <AdminDocStatusBadge status={status} />
                  </div>
                  <p className="text-sm font-medium truncate">{doc.original_filename || doc.label || doc.filename}</p>
                  <p className="text-xs text-muted-foreground mb-2">Uploaded {formatDocDate(doc.uploaded_at)}</p>
                  {status === 'rejected' && doc.rejection_reason && (
                    <p className="text-xs text-red-700 mb-2"><span className="font-medium">Reason:</span> {doc.rejection_reason}</p>
                  )}
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => viewDocument(doc.filename)}
                      data-testid={`admin-doc-view-${doc.id || doc.filename}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> View
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadDocument(doc.filename, doc.original_filename)}
                      data-testid={`admin-doc-download-${doc.id || doc.filename}`}>
                      <Download className="w-3.5 h-3.5 mr-1" /> Download
                    </Button>
                    {status !== 'approved' && (
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                        disabled={busyId === doc.id}
                        onClick={() => applyReview(doc, 'approved')}
                        data-testid={`admin-doc-approve-${doc.id || doc.filename}`}>
                        <Check className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                    )}
                    {status !== 'rejected' && (
                      <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50"
                        disabled={busyId === doc.id}
                        onClick={() => { setRejectingId(doc.id); setReason(''); }}
                        data-testid={`admin-doc-reject-${doc.id || doc.filename}`}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    )}
                  </div>
                  {rejectingId === doc.id && (
                    <div className="mt-3 space-y-2" data-testid={`admin-doc-reject-form-${doc.id}`}>
                      <Textarea
                        rows={2}
                        placeholder="Reason for rejection (shown to the facilitator)"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        data-testid={`admin-doc-reject-reason-${doc.id}`}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white"
                          disabled={busyId === doc.id || !reason.trim()}
                          onClick={() => applyReview(doc, 'rejected', reason.trim())}
                          data-testid={`admin-doc-reject-confirm-${doc.id}`}>
                          Confirm rejection
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setRejectingId(null); setReason(''); }}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  <DocumentHistory history={doc.history} testIdPrefix={`admin-doc-${doc.id || doc.filename}`} />
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};

// CSV Import Button Component
const CSVImportButton = ({ type, onSuccess }) => {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setResult(null);

    try {
      const response = type === 'facilitators' 
        ? await adminAPI.importFacilitators(file)
        : await adminAPI.importCamps(file);
      
      setResult(response.data);
      if (response.data.created > 0) {
        onSuccess?.();
      }
    } catch (err) {
      setResult({ status: 'error', error: err.response?.data?.detail || 'Import failed' });
    }
    
    setImporting(false);
    e.target.value = '';
  };

  return (
    <div className="relative">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImport}
        accept=".csv"
        className="hidden"
      />
      <Button
        variant="outline"
        className="rounded-full"
        onClick={() => fileInputRef.current?.click()}
        disabled={importing}
      >
        <Upload className="w-4 h-4 mr-2" />
        {importing ? 'Importing...' : 'Import CSV'}
      </Button>
      
      {result && (
        <Dialog open={!!result} onOpenChange={() => setResult(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Import Result</DialogTitle>
            </DialogHeader>
            {result.status === 'error' ? (
              <p className="text-destructive">{result.error}</p>
            ) : (
              <div className="space-y-2">
                <p className="text-primary font-medium">Created: {result.created} profiles</p>
                {result.total_errors > 0 && (
                  <div>
                    <p className="text-destructive">Errors: {result.total_errors}</p>
                    <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                      {result.errors?.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                      {result.total_errors > 5 && <li>... and {result.total_errors - 5} more</li>}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const ADMIN_TABS = ['facilitators', 'documents', 'camps', 'reviews', 'users', 'security'];

const DemoChip = () => (
  <span
    className="ml-2 align-middle px-1.5 py-0.5 rounded bg-sage/20 text-sage-dark text-[10px] font-bold uppercase tracking-wider"
    title="Internal demo listing — not a real submission"
    data-testid="demo-chip"
  >
    Demo
  </span>
);

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = ADMIN_TABS.includes(tabParam) ? tabParam : 'facilitators';
  const [pendingDocs, setPendingDocs] = useState(0);

  const loadPendingDocs = async () => {
    try {
      const res = await adminAPI.getPendingDocsCount();
      setPendingDocs(res.data?.pending || 0);
    } catch (err) {
      /* non-blocking */
    }
  };

  useEffect(() => {
    if (!authLoading && user?.role === 'admin') loadPendingDocs();
  }, [authLoading, user, activeTab]);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/login');
    }
  }, [user, authLoading]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" data-testid="admin-loading">
        <LogoMark size="xl" className="animate-pulse" />
        <div className="text-muted-foreground text-sm">Loading admin panel…</div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-8">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground">
              Admin Dashboard
            </h1>
            <p className="text-muted-foreground">Manage profiles, verification, and moderation</p>
          </div>
          {pendingDocs > 0 && (
            <button
              type="button"
              onClick={() => setSearchParams({ tab: 'documents' })}
              className="inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-amber-800 hover:bg-amber-100 transition-colors"
              data-testid="pending-docs-counter"
            >
              <Clock className="w-4 h-4" />
              <span className="text-sm font-semibold">{pendingDocs}</span>
              <span className="text-sm">document{pendingDocs === 1 ? '' : 's'} awaiting review</span>
            </button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setSearchParams({ tab: v })} className="space-y-6">
          <TabsList className="bg-muted/50 p-1 rounded-xl flex-wrap h-auto">
            <TabsTrigger value="facilitators" className="rounded-lg" data-testid="admin-tab-facilitators">
              <Users className="w-4 h-4 mr-2" />
              Facilitators
              {pendingDocs > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold" data-testid="facilitators-pending-badge">
                  {pendingDocs}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="documents" className="rounded-lg" data-testid="admin-tab-documents">
              <FileText className="w-4 h-4 mr-2" />
              Documents{pendingDocs > 0 ? ` (${pendingDocs})` : ''}
            </TabsTrigger>
            <TabsTrigger value="camps" className="rounded-lg">
              <Tent className="w-4 h-4 mr-2" />
              Camps
            </TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-lg">
              <MessageSquare className="w-4 h-4 mr-2" />
              Reviews
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg">
              <UserCog className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="security" className="rounded-lg" data-testid="admin-tab-security">
              <ShieldAlert className="w-4 h-4 mr-2" />
              Security
            </TabsTrigger>
          </TabsList>

          <TabsContent value="facilitators">
            <FacilitatorsTab onDocsChanged={loadPendingDocs} />
          </TabsContent>

          <TabsContent value="documents">
            <DocumentReviewQueueTab onDocsChanged={loadPendingDocs} />
          </TabsContent>

          <TabsContent value="camps">
            <CampsTab />
          </TabsContent>

          <TabsContent value="reviews">
            <ReviewsTab />
          </TabsContent>

          <TabsContent value="users">
            <UsersTab />
          </TabsContent>

          <TabsContent value="security">
            <SecurityTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const DocumentReviewQueueTab = ({ onDocsChanged }) => {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectingId, setRejectingId] = useState(null);
  const [reason, setReason] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    loadQueue();
  }, []);

  const loadQueue = async () => {
    try {
      const res = await adminAPI.getPendingDocsQueue();
      setQueue(res.data || []);
    } catch (err) {
      console.error('Failed to load document queue:', err);
    }
    setLoading(false);
  };

  const applyReview = async (item, status, rejectReason) => {
    setBusyId(item.document_id);
    try {
      await adminAPI.reviewDocument(item.facilitator_id, item.document_id, status, rejectReason || null);
      setQueue((prev) => prev.filter((d) => d.document_id !== item.document_id));
      setRejectingId(null);
      setReason('');
      toast.success(`Document ${status === 'approved' ? 'approved' : 'rejected'}`);
      onDocsChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update document');
    }
    setBusyId(null);
  };

  if (loading) {
    return <div className="text-muted-foreground text-sm py-8">Loading document queue…</div>;
  }

  return (
    <div className="bg-card rounded-2xl border border-border p-6" data-testid="documents-queue-tab">
      <div className="mb-5">
        <h2 className="font-heading font-semibold text-xl">Documents awaiting review</h2>
        <p className="text-sm text-muted-foreground">All pending verification documents across every facilitator — oldest waiting at the top.</p>
      </div>

      {queue.length === 0 ? (
        <div className="text-center py-10" data-testid="documents-queue-empty">
          <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No documents are waiting for review. You're all caught up.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="documents-queue-table">
            <TableHeader>
              <TableRow>
                <TableHead>Facilitator</TableHead>
                <TableHead>Document type</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {queue.map((item) => (
                <TableRow key={item.document_id} data-testid={`queue-row-${item.document_id}`}>
                  <TableCell className="font-medium">{item.facilitator_name}</TableCell>
                  <TableCell>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                      {docTypeLabel(item.type)}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDocDate(item.uploaded_at)}</TableCell>
                  <TableCell>
                    <div className="flex gap-2 justify-end flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => viewDocument(item.filename)}
                        data-testid={`queue-view-${item.document_id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> View
                      </Button>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                        disabled={busyId === item.document_id}
                        onClick={() => applyReview(item, 'approved')}
                        data-testid={`queue-approve-${item.document_id}`}>
                        <Check className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50"
                        disabled={busyId === item.document_id}
                        onClick={() => { setRejectingId(item.document_id); setReason(''); }}
                        data-testid={`queue-reject-${item.document_id}`}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                    </div>
                    {rejectingId === item.document_id && (
                      <div className="mt-3 space-y-2" data-testid={`queue-reject-form-${item.document_id}`}>
                        <Textarea
                          rows={2}
                          placeholder="Reason for rejection (shown to the facilitator)"
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          data-testid={`queue-reject-reason-${item.document_id}`}
                        />
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white"
                            disabled={busyId === item.document_id || !reason.trim()}
                            onClick={() => applyReview(item, 'rejected', reason.trim())}
                            data-testid={`queue-reject-confirm-${item.document_id}`}>
                            Confirm rejection
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setRejectingId(null); setReason(''); }}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};


const FacilitatorsTab = ({ onDocsChanged }) => {
  const [facilitators, setFacilitators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [docsFor, setDocsFor] = useState(null);

  useEffect(() => {
    loadFacilitators();
  }, []);

  const loadFacilitators = async () => {
    try {
      const response = await adminAPI.getFacilitators();
      setFacilitators(response.data);
    } catch (err) {
      console.error('Failed to load facilitators:', err);
    }
    setLoading(false);
  };

  const updateVerification = async (id, status) => {
    try {
      await adminAPI.updateFacilitator(id, { verification_status: status });
      loadFacilitators();
    } catch (err) {
      console.error('Failed to update:', err);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This cannot be undone.`)) return;
    try {
      await adminAPI.deleteFacilitator(id);
      alert('Facilitator deleted successfully');
      loadFacilitators();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete facilitator');
    }
  };

  const filtered = facilitators.filter(f => 
    f.full_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="font-heading font-semibold text-lg">Facilitators ({facilitators.length})</h2>
        <div className="flex gap-2">
          <CSVImportButton type="facilitators" onSuccess={loadFacilitators} />
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="create-facilitator-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Profile
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Unclaimed Facilitator Profile</DialogTitle>
                <DialogDescription>
                  Create a profile that can be claimed by the facilitator later.
                </DialogDescription>
              </DialogHeader>
              <CreateFacilitatorForm onSuccess={() => { setShowCreateDialog(false); loadFacilitators(); }} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search facilitators..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10 rounded-xl"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Claimed</TableHead>
                <TableHead>Provinces</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Docs</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">No facilitators found</TableCell>
                </TableRow>
              ) : (
                filtered.map((fac) => (
                  <TableRow key={fac.id}>
                    <TableCell className="font-medium">
                      <a
                        href={`/facilitators/${fac.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline underline-offset-2"
                        data-testid={`facilitator-name-link-${fac.id}`}
                      >
                        {fac.full_name}
                      </a>
                      {fac.is_demo && <DemoChip />}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${fac.profile_claimed ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {fac.profile_claimed ? 'Yes' : 'No'}
                      </span>
                    </TableCell>
                    <TableCell>{fac.provinces?.slice(0, 2).join(', ') || '-'}</TableCell>
                    <TableCell>
                      <TrustBadge status={fac.verification_status} size="small" />
                    </TableCell>
                    <TableCell>
                      {fac.documents?.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => setDocsFor(fac)}
                          className="text-primary text-sm underline underline-offset-2 hover:text-primary/80"
                          data-testid={`facilitator-docs-${fac.id}`}
                        >
                          {fac.documents.length} docs
                        </button>
                      ) : (
                        <span className="text-muted-foreground text-sm">None</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Select
                          value={fac.verification_status}
                          onValueChange={(v) => updateVerification(fac.id, v)}
                        >
                          <SelectTrigger className="w-28 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unverified">Unverified</SelectItem>
                            <SelectItem value="verified">Verified</SelectItem>
                            <SelectItem value="flagged">Flagged</SelectItem>
                          </SelectContent>
                        </Select>
                        <FacilitatorDetailDialog facilitator={fac} />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDelete(fac.id, fac.full_name)}
                          data-testid={`delete-facilitator-${fac.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <FacilitatorDocsDialog
        facilitator={docsFor}
        open={!!docsFor}
        onOpenChange={(v) => { if (!v) setDocsFor(null); }}
        onReviewed={() => { loadFacilitators(); onDocsChanged?.(); }}
      />
    </div>
  );
};

const CreateFacilitatorForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    bio: '',
    contact_email: '',
    contact_phone: '',
    provinces: [],
    camp_types: [],
    years_experience: 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      await adminAPI.createFacilitator(formData);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create profile');
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-destructive/10 text-destructive rounded-xl text-sm">{error}</div>
      )}
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Full Name *</Label>
          <Input
            value={formData.full_name}
            onChange={(e) => setFormData({...formData, full_name: e.target.value})}
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Years Experience</Label>
          <Input
            type="number"
            min="0"
            value={formData.years_experience}
            onChange={(e) => setFormData({...formData, years_experience: parseInt(e.target.value) || 0})}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Bio *</Label>
        <Textarea
          value={formData.bio}
          onChange={(e) => setFormData({...formData, bio: e.target.value.slice(0, 300)})}
          placeholder="Brief description..."
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Contact Email</Label>
          <Input
            type="email"
            value={formData.contact_email}
            onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
          />
        </div>
        <div className="space-y-2">
          <Label>Contact Phone</Label>
          <Input
            value={formData.contact_phone}
            onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Provinces</Label>
        <div className="flex flex-wrap gap-2">
          {provinces.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => {
                const arr = formData.provinces;
                setFormData({
                  ...formData,
                  provinces: arr.includes(p) ? arr.filter(x => x !== p) : [...arr, p]
                });
              }}
              className={`px-3 py-1 rounded-full text-xs ${
                formData.provinces.includes(p) ? 'bg-primary text-primary-foreground' : 'bg-muted'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <DialogFooter>
        <Button type="submit" disabled={saving}>
          {saving ? 'Creating...' : 'Create Profile'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const AdminReferencesPanel = ({ facilitator }) => {
  const [refs, setRefs] = useState(facilitator.references || []);

  useEffect(() => {
    setRefs(facilitator.references || []);
  }, [facilitator]);

  const save = async (refId, contacted, notes) => {
    try {
      await adminAPI.updateReference(facilitator.id, refId, contacted, notes);
      setRefs((prev) => prev.map((x) => (x.id === refId ? { ...x, contacted, notes } : x)));
      toast.success('Reference updated');
    } catch (err) {
      toast.error('Failed to update reference');
    }
  };

  const setLocalNotes = (refId, notes) => {
    setRefs((prev) => prev.map((x) => (x.id === refId ? { ...x, notes } : x)));
  };

  if (!refs.length) {
    return (
      <div data-testid="admin-references-panel">
        <span className="text-muted-foreground block mb-1">Professional References (internal):</span>
        <p className="text-xs text-muted-foreground">No references provided yet.</p>
      </div>
    );
  }
  return (
    <div data-testid="admin-references-panel">
      <span className="text-muted-foreground block mb-2">Professional References (internal — never shown publicly):</span>
      <div className="space-y-3">
        {refs.map((r, i) => (
          <div key={r.id || i} className="border border-border rounded-lg p-3" data-testid={`admin-reference-${i}`}>
            <p className="font-medium">{r.full_name} — {r.role}</p>
            <p className="text-xs text-muted-foreground">{r.organisation}</p>
            <p className="text-xs">Tel: {r.contact_number}{r.email ? ` · Email: ${r.email}` : ''}</p>
            <p className="text-xs italic text-muted-foreground mt-1">{r.relationship}</p>
            <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer">
              <Checkbox
                checked={!!r.contacted}
                onCheckedChange={(v) => save(r.id, !!v, r.notes || '')}
                data-testid={`reference-contacted-${i}`}
              />
              References contacted
            </label>
            <Textarea
              className="mt-2"
              rows={2}
              placeholder="Reference notes (outcome of the call)"
              value={r.notes || ''}
              onChange={(e) => setLocalNotes(r.id, e.target.value)}
              onBlur={(e) => save(r.id, !!r.contacted, e.target.value)}
              data-testid={`reference-notes-${i}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const FacilitatorDetailDialog = ({ facilitator }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Eye className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{facilitator.full_name}</DialogTitle>
          <DialogDescription>Facilitator Details (Admin View)</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-muted-foreground">Profile Claimed:</span>
              <span className="ml-2 font-medium">{facilitator.profile_claimed ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">ID Verified:</span>
              <span className="ml-2 font-medium">{facilitator.id_verified ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Police Clearance:</span>
              <span className="ml-2 font-medium capitalize">{facilitator.police_clearance?.replace('_', ' ')}</span>
            </div>
            <div>
              <span className="text-muted-foreground">First Aid Level:</span>
              <span className="ml-2 font-medium">{facilitator.first_aid_level || 'Not provided'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Background Check:</span>
              <span className="ml-2 font-medium">{facilitator.background_check_consent ? 'Consented' : 'Not consented'}</span>
            </div>
          </div>
          
          {facilitator.qualifications?.length > 0 && (
            <div>
              <span className="text-muted-foreground block mb-1">Qualifications:</span>
              <ul className="list-disc list-inside">
                {facilitator.qualifications.map((q, i) => (
                  <li key={i}>{q.name} ({q.year})</li>
                ))}
              </ul>
            </div>
          )}

          {facilitator.documents?.length > 0 && (
            <div>
              <span className="text-muted-foreground block mb-1">Uploaded Documents:</span>
              <ul className="space-y-1">
                {facilitator.documents.map((doc, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />
                    <button
                      type="button"
                      onClick={() => viewDocument(doc.filename)}
                      className="text-primary hover:underline"
                    >
                      {docTypeLabel(doc.type)} — {doc.original_filename || doc.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {facilitator.admin_notes && (
            <div>
              <span className="text-muted-foreground block mb-1">Admin Notes:</span>
              <p className="p-2 bg-muted rounded">{facilitator.admin_notes}</p>
            </div>
          )}

          <AdminReferencesPanel facilitator={facilitator} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CampsTab = () => {
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  useEffect(() => {
    loadCamps();
  }, []);

  const loadCamps = async () => {
    try {
      const response = await adminAPI.getCamps();
      setCamps(response.data);
    } catch (err) {
      console.error('Failed to load camps:', err);
    }
    setLoading(false);
  };

  const updateVerification = async (id, status) => {
    try {
      await adminAPI.verifyCamp(id, { verification_status: status });
      loadCamps();
    } catch (err) {
      console.error('Failed to update:', err);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This cannot be undone.`)) return;
    try {
      await adminAPI.deleteCamp(id);
      alert('Camp deleted successfully');
      loadCamps();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete camp');
    }
  };

  const filtered = camps.filter(c => 
    c.camp_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="font-heading font-semibold text-lg">Camps ({camps.length})</h2>
        <div className="flex gap-2">
          <CSVImportButton type="camps" onSuccess={loadCamps} />
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="create-camp-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Profile
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Unclaimed Camp Profile</DialogTitle>
                <DialogDescription>
                  Create a camp profile that can be claimed by the owner later.
                </DialogDescription>
              </DialogHeader>
              <CreateCampForm onSuccess={() => { setShowCreateDialog(false); loadCamps(); }} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search camps..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10 rounded-xl"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Claimed</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">No camps found</TableCell>
                </TableRow>
              ) : (
                filtered.map((camp) => (
                  <TableRow key={camp.id}>
                    <TableCell className="font-medium">
                      <a
                        href={`/camps/${camp.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline underline-offset-2"
                        data-testid={`camp-name-link-${camp.id}`}
                      >
                        {camp.camp_name}
                      </a>
                      {camp.is_demo && <DemoChip />}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${camp.profile_claimed ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {camp.profile_claimed ? 'Yes' : 'No'}
                      </span>
                    </TableCell>
                    <TableCell>{camp.nearest_town}, {camp.province}</TableCell>
                    <TableCell className="capitalize">{camp.camp_type}</TableCell>
                    <TableCell>
                      <TrustBadge status={camp.verification_status} size="small" />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Select
                          value={camp.verification_status}
                          onValueChange={(v) => updateVerification(camp.id, v)}
                        >
                          <SelectTrigger className="w-28 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unverified">Unverified</SelectItem>
                            <SelectItem value="verified">Verified</SelectItem>
                            <SelectItem value="flagged">Flagged</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDelete(camp.id, camp.camp_name)}
                          data-testid={`delete-camp-${camp.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

const CreateCampForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    camp_name: '',
    province: '',
    nearest_town: '',
    camp_type: '',
    description: '',
    contact_email: '',
    contact_phone: '',
    activities: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      await adminAPI.createCamp(formData);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create profile');
    }
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-destructive/10 text-destructive rounded-xl text-sm">{error}</div>
      )}
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Camp Name *</Label>
          <Input
            value={formData.camp_name}
            onChange={(e) => setFormData({...formData, camp_name: e.target.value})}
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Camp Type *</Label>
          <Select value={formData.camp_type || "select"} onValueChange={(v) => setFormData({...formData, camp_type: v === "select" ? "" : v})}>
            <SelectTrigger>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="select" disabled>Select type</SelectItem>
              {campTypes.map((ct) => (
                <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Province *</Label>
          <Select value={formData.province || "select"} onValueChange={(v) => setFormData({...formData, province: v === "select" ? "" : v})}>
            <SelectTrigger>
              <SelectValue placeholder="Select province" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="select" disabled>Select province</SelectItem>
              {provinces.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Nearest Town *</Label>
          <Input
            value={formData.nearest_town}
            onChange={(e) => setFormData({...formData, nearest_town: e.target.value})}
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          placeholder="Brief description of the camp..."
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Contact Email</Label>
          <Input
            type="email"
            value={formData.contact_email}
            onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
          />
        </div>
        <div className="space-y-2">
          <Label>Contact Phone</Label>
          <Input
            value={formData.contact_phone}
            onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Activities</Label>
        <div className="flex flex-wrap gap-2">
          {activities.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => {
                const arr = formData.activities;
                setFormData({
                  ...formData,
                  activities: arr.includes(a) ? arr.filter(x => x !== a) : [...arr, a]
                });
              }}
              className={`px-3 py-1 rounded-full text-xs ${
                formData.activities.includes(a) ? 'bg-primary text-primary-foreground' : 'bg-muted'
              }`}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      <DialogFooter>
        <Button type="submit" disabled={saving}>
          {saving ? 'Creating...' : 'Create Profile'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const ReviewsTab = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDisputedOnly, setShowDisputedOnly] = useState(false);
  const [expandedReview, setExpandedReview] = useState(null);

  useEffect(() => {
    loadReviews();
  }, [showDisputedOnly]);

  const loadReviews = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getReviews({ disputed_only: showDisputedOnly });
      setReviews(response.data);
    } catch (err) {
      console.error('Failed to load reviews:', err);
    }
    setLoading(false);
  };

  const handleApprove = async (id, approve) => {
    try {
      await adminAPI.approveReview(id, approve);
      loadReviews();
    } catch (err) {
      console.error('Failed to update review:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this review? This cannot be undone.')) return;
    try {
      await adminAPI.deleteReview(id);
      alert('Review deleted successfully');
      loadReviews();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete review');
    }
  };

  const handleDismissDispute = async (id) => {
    if (!window.confirm('Dismiss this dispute? The review will remain visible and the dispute flag will be removed.')) return;
    try {
      await adminAPI.dismissDispute(id);
      loadReviews();
    } catch (err) {
      console.error('Failed to dismiss dispute:', err);
    }
  };

  // Count disputed reviews
  const disputedCount = reviews.filter(r => r.disputed).length;

  return (
    <div className="space-y-4">
      {/* Filter Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowDisputedOnly(false)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              !showDisputedOnly 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            data-testid="show-all-reviews-btn"
          >
            All Reviews ({reviews.length})
          </button>
          <button
            onClick={() => setShowDisputedOnly(true)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
              showDisputedOnly 
                ? 'bg-amber-500 text-white' 
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            data-testid="show-disputed-reviews-btn"
          >
            <Flag className="w-4 h-4" />
            Disputed {!showDisputedOnly && disputedCount > 0 && `(${disputedCount})`}
          </button>
        </div>
      </div>

      {/* Disputed Reviews Alert */}
      {!showDisputedOnly && disputedCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <Flag className="w-5 h-5 text-amber-600" />
          <div>
            <p className="font-medium text-amber-800">
              {disputedCount} review{disputedCount !== 1 ? 's' : ''} flagged for dispute
            </p>
            <p className="text-sm text-amber-600">
              Profile owners have raised concerns about these reviews. Please review and take action.
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            className="ml-auto border-amber-300 text-amber-700 hover:bg-amber-100"
            onClick={() => setShowDisputedOnly(true)}
          >
            View Disputes
          </Button>
        </div>
      )}

      {/* Reviews Table */}
      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Reviewer</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead className="min-w-[200px]">Review</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : reviews.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    {showDisputedOnly ? 'No disputed reviews' : 'No reviews found'}
                  </TableCell>
                </TableRow>
              ) : (
                reviews.map((review) => (
                  <>
                    <TableRow key={review.id} className={review.disputed ? 'bg-amber-50/50' : ''}>
                      <TableCell className="font-medium">{review.reviewer_name}</TableCell>
                      <TableCell>
                        <span className="capitalize text-sm">
                          {review.reviewer_role || review.reviewer_type}
                        </span>
                      </TableCell>
                      <TableCell className="capitalize">{review.target_type}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          {review.rating}/5
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          <p className={`text-sm ${expandedReview === review.id ? '' : 'line-clamp-2'}`}>
                            {review.review_text}
                          </p>
                          {review.review_text?.length > 100 && (
                            <button
                              onClick={() => setExpandedReview(expandedReview === review.id ? null : review.id)}
                              className="text-xs text-primary mt-1"
                            >
                              {expandedReview === review.id ? 'Show less' : 'Show more'}
                            </button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium inline-block w-fit ${
                            review.approved 
                              ? 'bg-primary/10 text-primary' 
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {review.approved ? 'Approved' : 'Pending'}
                          </span>
                          {review.disputed && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 inline-block w-fit">
                              Disputed
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {!review.approved && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-primary"
                              onClick={() => handleApprove(review.id, true)}
                              title="Approve review"
                              data-testid={`approve-review-${review.id}`}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          {review.approved && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-muted-foreground"
                              onClick={() => handleApprove(review.id, false)}
                              title="Unapprove review"
                              data-testid={`unapprove-review-${review.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                          {review.disputed && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-amber-600"
                              onClick={() => handleDismissDispute(review.id)}
                              title="Dismiss dispute (keep review)"
                              data-testid={`dismiss-dispute-${review.id}`}
                            >
                              <ShieldCheck className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleDelete(review.id)}
                            title="Delete review permanently"
                            data-testid={`delete-review-${review.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {/* Dispute Details Row */}
                    {review.disputed && (
                      <TableRow key={`${review.id}-dispute`} className="bg-amber-50/30">
                        <TableCell colSpan={7} className="py-3">
                          <div className="flex items-start gap-3 pl-4">
                            <Flag className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-amber-800">Dispute Reason:</p>
                              <p className="text-sm text-amber-700 mt-1">{review.dispute_reason}</p>
                              {review.dispute_date && (
                                <p className="text-xs text-amber-500 mt-2">
                                  Disputed on {new Date(review.dispute_date).toLocaleDateString('en-ZA', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await adminAPI.getUsers();
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
    setLoading(false);
  };

  const handleSuspend = async (userId, suspend) => {
    try {
      await adminAPI.suspendUser(userId, suspend);
      loadUsers();
    } catch (err) {
      console.error('Failed to update user:', err);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This cannot be undone.')) return;
    try {
      await adminAPI.deleteUser(userId);
      alert('User deleted successfully');
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="font-heading font-semibold text-lg">Users ({users.length})</h2>
      
      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Email Verified</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">No users found</TableCell>
                </TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell className="capitalize">{u.role}</TableCell>
                    <TableCell>
                      {u.email_verified ? (
                        <span className="flex items-center gap-1 text-primary text-sm">
                          <MailCheck className="w-4 h-4" /> Yes
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-muted-foreground text-sm">
                          <Mail className="w-4 h-4" /> No
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        u.suspended ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
                      }`}>
                        {u.suspended ? 'Suspended' : 'Active'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {u.id !== currentUser?.id && (
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSuspend(u.id, !u.suspended)}
                            className="text-xs"
                          >
                            {u.suspended ? 'Activate' : 'Suspend'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleDelete(u.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

const EMPTY_INCIDENT = {
  discovered_date: '',
  description: '',
  action_taken: '',
  regulator_notified: false,
  regulator_notified_date: '',
  users_notified: false,
  users_notified_date: '',
  users_notified_method: '',
};

const SecurityTab = () => {
  const [incidents, setIncidents] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [devices, setDevices] = useState([]);
  const [history, setHistory] = useState([]);
  const [editingDeviceId, setEditingDeviceId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_INCIDENT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  // Deep-link from the alert email: /admin?tab=security#recent-signins
  useEffect(() => {
    if (loading) return;
    if (window.location.hash === '#recent-signins') {
      const el = document.getElementById('recent-signins');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('ring-2', 'ring-primary/40');
        setTimeout(() => el.classList.remove('ring-2', 'ring-primary/40'), 2500);
      }
    }
  }, [loading]);


  const loadData = async () => {
    setLoading(true);
    try {
      const [incRes, logRes, devRes, histRes] = await Promise.all([
        adminAPI.getIncidents(),
        adminAPI.getAuditLogs({ limit: 100 }),
        adminAPI.getTrustedDevices(),
        adminAPI.getLoginHistory(),
      ]);
      setIncidents(incRes.data);
      setAuditLogs(logRes.data);
      setDevices(devRes.data);
      setHistory(histRes.data);
    } catch (err) {
      console.error('Failed to load security data', err);
    }
    setLoading(false);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_INCIDENT);
    setError('');
    setShowDialog(true);
  };

  const openEdit = (inc) => {
    setEditingId(inc.id);
    setForm({
      discovered_date: inc.discovered_date || '',
      description: inc.description || '',
      action_taken: inc.action_taken || '',
      regulator_notified: !!inc.regulator_notified,
      regulator_notified_date: inc.regulator_notified_date || '',
      users_notified: !!inc.users_notified,
      users_notified_date: inc.users_notified_date || '',
      users_notified_method: inc.users_notified_method || '',
    });
    setError('');
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.discovered_date || !form.description.trim()) {
      setError('Discovery date and description are required.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      if (editingId) {
        await adminAPI.updateIncident(editingId, form);
      } else {
        await adminAPI.createIncident(form);
      }
      setShowDialog(false);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save incident.');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this incident record? This cannot be undone.')) return;
    try {
      await adminAPI.deleteIncident(id);
      await loadData();
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const yesNo = (v) => (v ? 'Yes' : 'No');

  const downloadBlob = (res, fallback) => {
    const dispo = res.headers['content-disposition'] || '';
    const m = dispo.match(/filename="?([^"]+)"?/);
    const name = m ? m[1] : fallback;
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const [exporting, setExporting] = useState('');

  const handleExportAudit = async () => {
    setExporting('audit');
    try {
      const res = await adminAPI.exportAuditLogs();
      downloadBlob(res, 'campia-audit-log.csv');
      await loadData();
    } catch (e) { console.error(e); }
    setExporting('');
  };

  const handleExportIncidents = async () => {
    setExporting('incidents');
    try {
      const res = await adminAPI.exportIncidents();
      downloadBlob(res, 'campia-incidents.csv');
      await loadData();
    } catch (e) { console.error(e); }
    setExporting('');
  };

  const handleCompliancePack = async () => {
    setExporting('pack');
    try {
      const res = await adminAPI.downloadCompliancePack();
      downloadBlob(res, 'campia-popia-compliance-pack.pdf');
      await loadData();
    } catch (e) { console.error(e); }
    setExporting('');
  };

  const handleRevokeDevice = async (id) => {
    if (!window.confirm('Revoke this trusted device? It will need a login code next time.')) return;
    try {
      await adminAPI.revokeTrustedDevice(id);
      await loadData();
    } catch (e) { console.error(e); }
  };

  const handleRevokeAllDevices = async () => {
    if (!window.confirm('Revoke ALL trusted devices? Every device will need a fresh login code.')) return;
    try {
      await adminAPI.revokeAllTrustedDevices();
      await loadData();
    } catch (e) { console.error(e); }
  };

  const startEditLabel = (d) => {
    setEditingDeviceId(d.id);
    setEditLabel(d.label || '');
  };

  const saveLabel = async (id) => {
    const label = editLabel.trim();
    if (label) {
      try {
        await adminAPI.renameTrustedDevice(id, label);
        toast.success('Device renamed');
        await loadData();
      } catch (e) {
        toast.error('Could not rename device');
        console.error(e);
      }
    }
    setEditingDeviceId(null);
    setEditLabel('');
  };

  return (
    <div className="space-y-8" data-testid="security-tab">
      {/* POPIA Incident Log */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
          <div>
            <h2 className="font-heading font-semibold text-xl flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-primary" />
              Security Incident Log
            </h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              POPIA Section 22 requires that data breaches be documented and the Information
              Regulator and affected users notified as soon as reasonably possible. Record every
              incident here for your compliance trail.
            </p>
          </div>
          <Button onClick={openCreate} data-testid="add-incident-button">
            <Plus className="w-4 h-4 mr-2" />
            Record Incident
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={handleExportIncidents}
            disabled={exporting === 'incidents'} data-testid="export-incidents-button">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {exporting === 'incidents' ? 'Exporting…' : 'Export Incidents CSV'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleCompliancePack}
            disabled={exporting === 'pack'} data-testid="compliance-pack-button"
            className="border-primary/40 text-primary hover:bg-primary/5">
            <FileDown className="w-4 h-4 mr-2" />
            {exporting === 'pack' ? 'Generating…' : 'Download Compliance Pack'}
          </Button>
        </div>

        <div className="mt-4 overflow-x-auto">
          {loading ? (
            <p className="text-muted-foreground text-sm py-6">Loading…</p>
          ) : incidents.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground" data-testid="no-incidents">
              <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-primary/40" />
              <p>No incidents recorded. That's a good thing.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Discovered</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Regulator</TableHead>
                  <TableHead>Users notified</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {incidents.map((inc) => (
                  <TableRow key={inc.id} data-testid={`incident-row-${inc.id}`}>
                    <TableCell className="whitespace-nowrap">{inc.discovered_date}</TableCell>
                    <TableCell className="max-w-xs">
                      <div className="line-clamp-2">{inc.description}</div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {yesNo(inc.regulator_notified)}
                      {inc.regulator_notified && inc.regulator_notified_date ? ` · ${inc.regulator_notified_date}` : ''}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {yesNo(inc.users_notified)}
                      {inc.users_notified && inc.users_notified_date ? ` · ${inc.users_notified_date}` : ''}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8"
                          onClick={() => openEdit(inc)} data-testid={`edit-incident-${inc.id}`}>
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                          onClick={() => handleDelete(inc.id)} data-testid={`delete-incident-${inc.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Trusted devices */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-heading font-semibold text-xl flex items-center gap-2 mb-1">
              <MonitorSmartphone className="w-5 h-5 text-primary" />
              Trusted Devices
            </h2>
            <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
              Devices where you chose "Trust this device for 30 days" skip the email login code.
              Revoke any you no longer recognise — they'll need a fresh code next time.
            </p>
          </div>
          {devices.length > 0 && (
            <Button variant="outline" size="sm" onClick={handleRevokeAllDevices}
              className="text-destructive border-destructive/40 hover:bg-destructive/5" data-testid="revoke-all-devices-button">
              Revoke all
            </Button>
          )}
        </div>
        {devices.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="no-trusted-devices">
            <MonitorSmartphone className="w-9 h-9 mx-auto mb-2 text-primary/40" />
            <p className="text-sm">No trusted devices. Every admin login currently requires a code.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Device</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>Last used</TableHead>
                  <TableHead>Trusted until</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {devices.map((d) => (
                  <TableRow key={d.id} data-testid={`device-row-${d.id}`}>
                    <TableCell className="max-w-xs">
                      {editingDeviceId === d.id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={editLabel}
                            onChange={(e) => setEditLabel(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveLabel(d.id); if (e.key === 'Escape') setEditingDeviceId(null); }}
                            className="h-8 text-sm"
                            autoFocus
                            maxLength={60}
                            data-testid={`device-label-input-${d.id}`}
                          />
                          <Button size="sm" className="h-8" onClick={() => saveLabel(d.id)} data-testid={`device-label-save-${d.id}`}>Save</Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium text-sm">{d.label}</div>
                            <div className="text-xs text-muted-foreground truncate max-w-[200px]">{d.user_agent}</div>
                          </div>
                          <button type="button" onClick={() => startEditLabel(d)}
                            className="text-muted-foreground hover:text-primary" title="Rename"
                            data-testid={`device-rename-${d.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{d.ip || '—'}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{new Date(d.last_used).toLocaleString('en-ZA')}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{new Date(d.expires_at).toLocaleDateString('en-ZA')}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="h-8 text-destructive"
                        onClick={() => handleRevokeDevice(d.id)} data-testid={`revoke-device-${d.id}`}>
                        Revoke
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Recent sign-ins */}
      <div id="recent-signins" className="bg-card rounded-2xl border border-border p-6 scroll-mt-24 transition-shadow">
        <h2 className="font-heading font-semibold text-xl flex items-center gap-2 mb-1">
          <History className="w-5 h-5 text-primary" />
          Recent Sign-ins
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Your last 10 admin sign-in attempts (most recent first).
        </p>
        <div className="overflow-x-auto">
          {history.length === 0 ? (
            <p className="text-muted-foreground text-sm py-6" data-testid="no-signins">No sign-ins recorded yet.</p>
          ) : (
            <Table data-testid="signins-table">
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((h, i) => (
                  <TableRow key={`${h.timestamp}-${i}`} data-testid={`signin-row-${i}`}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {new Date(h.timestamp).toLocaleString('en-ZA')}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{h.ip || '—'}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{h.location || 'Unknown'}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{h.device || '—'}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{h.method || '—'}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${h.success ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'}`}>
                        {h.success ? 'Success' : 'Failed'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Audit trail */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-heading font-semibold text-xl flex items-center gap-2 mb-1">
              <ScrollText className="w-5 h-5 text-primary" />
              Audit Trail
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Recent admin actions, document views and failed login attempts (most recent first).
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={handleExportAudit}
            disabled={exporting === 'audit'} data-testid="export-audit-button">
            <Download className="w-4 h-4 mr-2" />
            {exporting === 'audit' ? 'Exporting…' : 'Export CSV'}
          </Button>
        </div>
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
          {auditLogs.length === 0 ? (
            <p className="text-muted-foreground text-sm py-6">No activity logged yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Detail</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {new Date(log.timestamp).toLocaleString('en-ZA')}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        log.event === 'failed_login'
                          ? 'bg-destructive/10 text-destructive'
                          : log.event === 'document_access'
                          ? 'bg-amber-500/10 text-amber-700'
                          : 'bg-primary/10 text-primary'
                      }`}>
                        {log.event}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{log.actor_email || '—'}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {log.target_type ? `${log.target_type}${log.target_id ? ` · ${String(log.target_id).slice(0, 8)}` : ''}` : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{log.detail || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Incident dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Incident' : 'Record Security Incident'}</DialogTitle>
            <DialogDescription>Internal POPIA breach record. Kept for compliance only.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="inc-date">Date discovered *</Label>
              <Input id="inc-date" type="date" value={form.discovered_date} data-testid="incident-date-input"
                onChange={(e) => { setForm({ ...form, discovered_date: e.target.value }); setError(''); }} />
            </div>
            <div>
              <Label htmlFor="inc-desc">What was exposed? *</Label>
              <Textarea id="inc-desc" rows={3} value={form.description} data-testid="incident-description-input"
                placeholder="Describe the data that was exposed and how"
                onChange={(e) => { setForm({ ...form, description: e.target.value }); setError(''); }} />
            </div>
            <div>
              <Label htmlFor="inc-action">Action taken</Label>
              <Textarea id="inc-action" rows={2} value={form.action_taken} data-testid="incident-action-input"
                placeholder="Steps taken to contain and remediate"
                onChange={(e) => setForm({ ...form, action_taken: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Information Regulator notified?</Label>
                <Select value={form.regulator_notified ? 'yes' : 'no'}
                  onValueChange={(v) => setForm({ ...form, regulator_notified: v === 'yes' })}>
                  <SelectTrigger data-testid="incident-regulator-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="reg-date">Date notified</Label>
                <Input id="reg-date" type="date" value={form.regulator_notified_date} data-testid="incident-regulator-date-input"
                  disabled={!form.regulator_notified}
                  onChange={(e) => setForm({ ...form, regulator_notified_date: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Users notified?</Label>
                <Select value={form.users_notified ? 'yes' : 'no'}
                  onValueChange={(v) => setForm({ ...form, users_notified: v === 'yes' })}>
                  <SelectTrigger data-testid="incident-users-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">No</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="usr-date">Date notified</Label>
                <Input id="usr-date" type="date" value={form.users_notified_date} data-testid="incident-users-date-input"
                  disabled={!form.users_notified}
                  onChange={(e) => setForm({ ...form, users_notified_date: e.target.value })} />
              </div>
            </div>
            {form.users_notified && (
              <div>
                <Label htmlFor="usr-method">Notification method</Label>
                <Input id="usr-method" value={form.users_notified_method} data-testid="incident-users-method-input"
                  placeholder="e.g. Email, SMS, in-app notice"
                  onChange={(e) => setForm({ ...form, users_notified_method: e.target.value })} />
              </div>
            )}
            {error && <p className="text-sm text-destructive" data-testid="incident-error">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-incident-button">
              {saving ? 'Saving…' : editingId ? 'Update' : 'Save Record'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
